import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import {
  type PaymentAuditEvent,
  PEBLPaymentLink,
  PEBLPaymentRequest,
} from '../transactions-layout/models/pebl-payment';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ConfirmationService,
  NotificationService,
  VTService
} from '@odin/odin-core';
import { RoleGuardService } from 'libs/odin-authentication/src/lib/services/role-guard.service';
import { AccessGuardService } from 'libs/odin-authentication/src/lib/services/access-guard.service';

@Component({
  standalone: false,
  selector: 'odin-pebl-tracking-details',
  templateUrl: './pebl-tracking-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pebl-tracking-details.component.scss'],
})
export class PeblTrackingDetailsComponent {
  public actionButtonLoading: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public trackedLink: PEBLPaymentLink,
    public dialogRef: MatDialogRef<PeblTrackingDetailsComponent>,
    private notificationService: NotificationService,
    private vtService: VTService,
    private confirmationService: ConfirmationService,
    private accessService: AccessGuardService,
    private roleGuardService: RoleGuardService,
    private router: Router,
  ) {}


  public GrantChecker(): boolean {
    return (
      this.accessService.hasNewGrant('txn:CreatePebl') || this.roleGuardService.hasPermission('pbl:Create')
    );
  }

  public CancelLink(): void {
    this.notificationService.InputDialog(
      {
        placeholder: 'Reason',
        readonly: false,
        title: 'Cancellation Reason',
        subHeading: 'Provide a reason this link is being cancelled',
        defaultValue: '',
        allowCopy: false,
      },
      (reason: string) => {
        if (!reason || reason.trim().length === 0) {
          this.notificationService.SmallDialog(
            'Cancellation reason is required to cancel the link.',
            'Close',
            3000,
          );
          return;
        }

        this.confirmationService
          .ConfirmDecline(
            'Cancel Link',
            'Are you sure you want to cancel this link?',
            'Confirm',
            'Cancel',
            'primary',
            'warn',
          )
          .subscribe((confirmed: boolean) => {
            if (!confirmed) return;
            this.actionButtonLoading = true;
            this.vtService
              .CancelPaymentLink(this.trackedLink, reason)
              .subscribe(
                () => {
                  this.actionButtonLoading = false;
                  this.dialogRef.close(true);
                },
                (error: { error: { error: { message: string } } }) => {
                  try {
                    this.notificationService.ErrorSmallDialog(
                      `Failed to cancel: ${error.error.error.message}`,
                      error,
                    );
                  } catch {
                    this.notificationService.ErrorSmallDialog(
                      'An unknown error occured, please try again.',
                      error,
                    );
                  }
                  this.actionButtonLoading = false;
                },
              );
          });
      },
      {
        panelClass: ['main-panel'],
        maxWidth: '400px',
        autoFocus: false,
      },
    );
  }

  public ReinstateLink(): void {
    this.confirmationService
      .ConfirmDecline(
        'Reinstate Link',
        'Are you sure you want to re-create this link? The customer will be notified.',
        'Confirm',
        'Cancel',
        'primary',
        'warn',
      )
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.actionButtonLoading = true;
        const request = new PEBLPaymentRequest().copy(this.trackedLink);
        this.vtService.CreatePeblRequest(request).subscribe(
          () => {
            this.actionButtonLoading = false;
            this.dialogRef.close(true);
          },
          (error: unknown) => {
            this.actionButtonLoading = false;
            this.notificationService.ErrorSmallDialog(
              'Failed to reinstate link',
              error,
            );
          },
        );
      });
  }

  public hasExpired(): boolean {
    const expiration = new Date(this.trackedLink.linkExpiration);
    const now = new Date();
    if (expiration < now) return true;
    if (this.trackedLink.status === 'Cancelled') return true;
    if (this.trackedLink.status === 'Expired') return true;
    return false;
  }

  public deliveryIcon(): string {
    switch (this.trackedLink.delivery) {
      case 'email':
        return 'email';
      case 'sms':
        return 'phone_iphone';
      case 'manual':
        return 'assignment_turned_in';
    }
  }

  public deliveryName(): string {
    switch (this.trackedLink.delivery) {
      case 'email':
        return 'E-mail';
      case 'sms':
        return 'SMS';
      case 'manual':
        return 'Copied';
    }
  }

  public deliveryExpiryIcon(): string {
    if (this.trackedLink.status === 'Authorised') return 'credit_score';
    if (this.trackedLink.status === 'Cancelled') return 'cancel';
    const expiration = new Date(this.trackedLink.linkExpiration);
    const now = new Date();
    if (expiration < now) return 'hourglass_disabled';
    if (Math.abs(expiration.getTime() - now.getTime()) / 3600000 < 24)
      return 'hourglass_bottom';
    return 'hourglass_full';
  }

  public deliveryExpiryClass(): string {
    if (this.trackedLink.status == 'Authorised') return 'text-success';
    if (this.trackedLink.status === 'Cancelled') return 'text-danger';

    const expiration = new Date(this.trackedLink.linkExpiration);
    const now = new Date();

    if (expiration < now) return 'text-danger';
    if (Math.abs(expiration.getTime() - now.getTime()) / 3600000 < 24)
      return 'text-warning';
    return 'text-success';
  }

  public statusIcon(): string {
    switch (this.statusText()) {
      case 'Paid':
        return 'check_circle';
      case 'Cancelled':
        return 'cancel';
      case 'Expired':
        return 'hourglass_disabled';
      default:
        return 'hourglass_top';
    }
  }

  public statusBadgeClass(): string {
    switch (this.statusText()) {
      case 'Paid':
        return 'mon-bg-success mon-text-success';
      case 'Cancelled':
      case 'Expired':
        return 'mon-bg-danger mon-text-danger';
      default:
        return 'mon-bg-warning mon-text-warning';
    }
  }

  public statusText(): string {
    const expiration = new Date(this.trackedLink.linkExpiration);
    const now = new Date();

    if (this.trackedLink.status === 'Cancelled') {
      return 'Cancelled';
    }

    if (this.trackedLink.status !== 'Authorised' && expiration < now) {
      return 'Expired';
    }

    switch (this.trackedLink.status) {
      case 'Sent':
        return 'Unpaid';
      case 'Expired':
        return 'Expired';
      case 'Authorised':
        return 'Paid';
    }
  }

  public isAuditEventPaid(event: PaymentAuditEvent): boolean {
    return event.status === 'Authorised';
  }

  public auditEventIcon(event: PaymentAuditEvent): string {
    return this.isAuditEventPaid(event) ? 'check_circle' : 'cancel';
  }

  public auditEventClass(event: PaymentAuditEvent): string {
    return this.isAuditEventPaid(event)
      ? 'mon-text-success'
      : 'mon-text-danger';
  }

  public hasAuditEventDetails(event: PaymentAuditEvent): boolean {
    return !!event.crossReference;
  }

  public viewAuditEventTransaction(event: PaymentAuditEvent): void {
    if (!event.crossReference) return;
    this.dialogRef.close();
    this.router.navigate([`/transactions/detail/${event.crossReference}`]);
  }

  public copyLink(): void {
    navigator.clipboard.writeText(this.trackedLink.link).then(() => {
      this.notificationService.SmallDialog(
        'Copied to clipboard',
        'Close',
        3000,
      );
    });
  }
}
